/**
 * ZIP reading of the offline import, on top of zip.js: lists the DICOM files
 * of an archive and extracts one of them in memory.
 */
// Deep imports: webpack 4 cannot parse import.meta; switch to lib/zip-core-custom.js with webpack 5
import { ZipReader } from '@zip.js/zip.js/lib/zip-core-reader.js';
import { BlobReader, Uint8ArrayWriter } from '@zip.js/zip.js/lib/core/io.js';
import { configure } from '@zip.js/zip.js/lib/core/configuration.js';

configure({ useWebWorkers: false });

// Rethrow an AbortError unchanged, any other error as a malformed archive
const throwArchiveError = (archive, error) => {
  if (error.name === 'AbortError') {
    throw error;
  }

  throw new Error(`Malformed archive ${archive.name}: ${error.message}`);
};

/**
 * List the DICOM files of an archive, from its central directory. The reader
 * is not closed: with a BlobReader `close()` is a no-op and the DICOM files
 * keep reading through it.
 * @param {File} archive The archive
 * @returns {Promise<object[]>} The DICOM files, as zip.js entries
 */
export const listDicoms = archive => {
  const reader = new ZipReader(new BlobReader(archive));

  return reader.getEntries().catch(error => throwArchiveError(archive, error));
};

/**
 * Extract one DICOM in memory (Firefox and Safari cannot stream request
 * bodies).
 * @param {File} archive The archive, for the error message
 * @param {object} dicom A DICOM file of the zip.js reader
 * @param {AbortSignal} signal Abort signal of the run
 * @returns {Promise<Uint8Array>} Its uncompressed content
 */
export const extractDicom = (archive, dicom, signal) =>
  dicom
    .getData(new Uint8ArrayWriter(), { signal })
    .catch(error => throwArchiveError(archive, error));
